import {useContext, useState} from 'react';

import {Archive} from '@mui/icons-material';
import {Button, CircularProgress, Tooltip} from '@mui/material';
import JSZip from 'jszip';

import {request, URLs} from '../../../api';
import {GroupContestViewDataQueryResponse} from '../../../api/responses/ContestViewResponseTypes';
import {formatYYYYMMDDHHMMSS, fromDateToSmall} from '../../../utils/date-utils';
import AppContext from '../../../context/AppContext';
import {
  ContestSubmissionForCodeArchiveExportQueryParams,
  ContestSubmissionForCodeArchiveExportResponse,
} from '../../../api/responses/GroupSubmissionsExportTypes';

type SubmissionsArchiveButtonProps = {
  contest: GroupContestViewDataQueryResponse;
  groupContest: number;
  searchParams: {
    problemId?: number;
    userId?: number;
    total?: number;
    latest?: boolean;
    duringContest?: boolean;
  };
};

function SubmissionsArchivedExportButton(props: SubmissionsArchiveButtonProps) {
  const {dispatchError} = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);

  const prepareArchiveAndExport = (data: ContestSubmissionForCodeArchiveExportResponse) => {
    const zip = new JSZip();

    data.forEach((row) => {
      const problemName = row.problemName || 'Unknown problem name';

      let fileExtension;

      switch (row.languageId) {
        case 1:
          fileExtension = 'java';
          break;
        case 2:
          fileExtension = 'c';
          break;
        case 3:
          fileExtension = 'cpp';
          break;
        case 4:
          fileExtension = 'py';
          break;
        default:
          fileExtension = 'txt';
      }

      const fileName =
        `${row.userEmail.split('@')[0]}-${problemName.replaceAll(' ', '_')}-${formatYYYYMMDDHHMMSS(
          new Date(row.submittedAt)
        )}.` + fileExtension;

      zip.file(fileName, row.code);
    });

    zip
      .generateAsync({type: 'blob'})
      .then(function (blob: Blob | MediaSource) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${props.contest.title}-${fromDateToSmall(new Date())}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      })
      .catch(function (error: Error) {
        console.error('Error generating zip file:', error);
      });
  };

  const archiveSubmissions = async () => {
    const searchParams = new URLSearchParams();

    Object.entries({
      ...props.searchParams,
    }).forEach(([key, value]) => {
      if (value != null) {
        searchParams.append(key, value.toString());
      }
    });

    setIsLoading(true);
    await request<
      ContestSubmissionForCodeArchiveExportQueryParams,
      never,
      ContestSubmissionForCodeArchiveExportResponse
    >(URLs.getAllGroupContestSubmissionsForCodeArchiveExport(Number(props.groupContest)), {
      method: 'GET',
      params: {
        duringContest: props.searchParams.duringContest ? 'true' : 'false',
        latest: props.searchParams.latest ? 'true' : 'false',
        problemId: props.searchParams.problemId ? String(props.searchParams.problemId) : undefined,
        userId: props.searchParams.userId ? String(props.searchParams.userId) : undefined,
      },
      successCallback(response) {
        prepareArchiveAndExport(response);
        setIsLoading(false);
      },
      errorCallback() {
        dispatchError({errorMessage: 'There was an error. Please try again later'});
        setIsLoading(false);
      },
    });
  };

  return (
    <>
      <Tooltip title="Exports achvied files containing the submitted code based on the selected filters">
        <Button
          startIcon={isLoading ? <CircularProgress color="inherit" size="1.1rem" /> : <Archive />}
          size="small"
          color="primary"
          variant="contained"
          onClick={() => archiveSubmissions()}
        >
          Export to Code Archive
        </Button>
      </Tooltip>
    </>
  );
}

export default SubmissionsArchivedExportButton;
